import React, { useMemo, useEffect } from "react";
import { Box, Tooltip, Skeleton } from "@mui/material";
import Avatar from "./Avatar";
import useUsers from "../hooks/useUsers/useUsers";
import { User } from "../types/user";

interface AvatarStackProps {
  users?: User[]; // Pre-populated user data
  userEmails?: string[]; // Email addresses to fetch user data for
  currentUserEmail?: string; // Email of the current user to prioritize first
  max?: number; // Maximum number of avatars to show
  showTooltip?: boolean; // Whether to show tooltips with user names
  variant?: "circular" | "rounded" | "square"; // Avatar variant
  useCache?: boolean; // Whether to use the caching hook
  sx?: object; // Optional sx prop for custom styles
  avatarSx?: object; // Optional sx prop for custom styles
  tooltipLimit?: number; // Maximum number of names to show in overflow tooltip
  emptyPlaceholder?: React.ReactNode; // Optional placeholder when no users (default: blank)
  forceLoading?: boolean; // Force loading state (useful for "before game starts" scenarios)
}

/**
 * Custom hook to optimize user data for AvatarStack
 * This helps with deduplication and data normalization
 */
const useOptimizedUsers = (users: User[], currentUserEmail?: string) => {
  return useMemo(() => {
    if (!users || users.length === 0) return [];

    // Remove duplicates based on email
    const uniqueUsers = users.filter(
      (user, index, self) =>
        index === self.findIndex((u) => u.email === user.email)
    );

    // Sort users to ensure consistent ordering, with current user first if provided
    return uniqueUsers.sort((a, b) => {
      // If current user email is provided, prioritize it first
      if (currentUserEmail) {
        if (a.email === currentUserEmail && b.email !== currentUserEmail)
          return -1;
        if (b.email === currentUserEmail && a.email !== currentUserEmail)
          return 1;
      }
      // Otherwise, sort alphabetically by email
      return a.email.localeCompare(b.email);
    });
  }, [users, currentUserEmail]);
};

const AvatarStack: React.FC<AvatarStackProps> = ({
  users: providedUsers,
  userEmails,
  currentUserEmail,
  max = 4,
  showTooltip = true,
  variant = "circular",
  useCache = false,
  sx = {},
  avatarSx = {},
  tooltipLimit = 10,
  emptyPlaceholder,
  forceLoading = false,
}) => {
  const { areUsersLoading, preloadUsers, getCachedUsers } = useUsers();

  // Determine which users to use
  const finalUsers = useMemo(() => {
    if (providedUsers) return providedUsers;
    if (useCache && userEmails) {
      return getCachedUsers(userEmails);
    }
    return [];
  }, [providedUsers, useCache, userEmails, getCachedUsers]);

  const optimizedUsers = useOptimizedUsers(finalUsers, currentUserEmail);

  // Show overflow indicator only if users exceed max + 1.
  // If users === max + 1, showing the extra avatar takes the same space as a "+1" badge,
  // so we display the avatar directly instead of showing a "+1" overflow badge.
  const hasOverflow = optimizedUsers.length > max + 1;

  // Calculate remaining users for overflow tooltip
  const remainingUsers = useMemo(() => {
    if (!hasOverflow) return [];
    return optimizedUsers.slice(max).slice(0, tooltipLimit);
  }, [optimizedUsers, max, tooltipLimit, hasOverflow]);

  const visibleUsers = useMemo(() => {
    if (hasOverflow) {
      return optimizedUsers.slice(0, max);
    }
    return optimizedUsers;
  }, [optimizedUsers, max, hasOverflow]);

  // Create tooltip content for overflow avatar
  const overflowTooltipContent = useMemo(() => {
    if (!hasOverflow || remainingUsers.length === 0) return "";

    const names = remainingUsers.map((user) => user.displayName || user.email);
    const visibleCount = max;
    const hasMore = optimizedUsers.length - visibleCount > tooltipLimit;

    let content = names.join(", ");
    if (hasMore) {
      const additionalCount =
        optimizedUsers.length - visibleCount - tooltipLimit;
      content += `, and ${additionalCount} more`;
    }

    return content;
  }, [hasOverflow, remainingUsers, optimizedUsers.length, max, tooltipLimit]);

  // Preload users if caching is enabled
  useEffect(() => {
    if (useCache && userEmails) {
      preloadUsers(userEmails);
    }
  }, [useCache, userEmails, preloadUsers]);

  // Show loading state - show skeleton until we have actual user data
  const shouldShowLoading =
    forceLoading || (useCache && userEmails && areUsersLoading(userEmails));

  if (shouldShowLoading) {
    // Calculate skeleton dimensions based on avatar size
    const avatarSize =
      (avatarSx as any)?.width || (avatarSx as any)?.height || 64;
    const avatarCount = Math.min(max, userEmails?.length || 3);

    // Calculate width: avatar size * number of avatars, with some overlap
    const overlap = avatarSize * 0.3; // 30% overlap
    const width = avatarSize + (avatarCount - 1) * (avatarSize - overlap);

    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Skeleton
          variant="rectangular"
          width={width}
          height={avatarSize}
          sx={{
            borderRadius: avatarSize / 2, // Make it circular like avatars
            ...sx,
          }}
        />
      </Box>
    );
  }

  // If no users, return empty
  if (optimizedUsers.length === 0) {
    if (emptyPlaceholder !== undefined && emptyPlaceholder !== null) {
      return <>{emptyPlaceholder}</>;
    }
    return null; // Default to blank when no users
  }

  // If only one user, return single avatar with consistent styling
  if (optimizedUsers.length === 1) {
    const user = optimizedUsers[0];
    const avatar = (
      <Avatar
        email={user.email}
        auth0Picture={user.auth0Picture}
        alt={user.displayName || user.email}
        sx={{
          ...avatarSx,
          border: `2px solid white`,
          transition: "transform 0.2s ease-in-out",
          "&:hover": {
            zIndex: 10,
            transform: "scale(1.1)",
          },
        }}
        showSkeleton={false}
      />
    );

    return showTooltip ? (
      <Tooltip title={user.displayName || user.email}>{avatar}</Tooltip>
    ) : (
      avatar
    );
  }

  // Create the overflow avatar with tooltip if needed
  const overflowAvatar = hasOverflow ? (
    showTooltip ? (
      <Tooltip title={overflowTooltipContent} arrow placement="top">
        <Box
          sx={{
            width: (avatarSx as any)?.width || 64,
            height: (avatarSx as any)?.height || 64,
            borderRadius: "50%",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "grey.700" : "grey.300",
            color: (theme) =>
              theme.palette.mode === "dark" ? "grey.100" : "grey.800",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: "0.875rem",
            border: `2px solid white`,
            transition: "transform 0.2s ease-in-out",
            "&:hover": {
              zIndex: 10,
              transform: "scale(1.1)",
            },
          }}
        >
          +{optimizedUsers.length - max}
        </Box>
      </Tooltip>
    ) : (
      <Box
        sx={{
          width: (avatarSx as any)?.width || 64,
          height: (avatarSx as any)?.height || 64,
          borderRadius: "50%",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "grey.700" : "grey.300",
          color: (theme) =>
            theme.palette.mode === "dark" ? "grey.100" : "grey.800",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "bold",
          fontSize: "0.875rem",
          border: `2px solid white`,
          transition: "transform 0.2s ease-in-out",
          "&:hover": {
            zIndex: 10,
            transform: "scale(1.1)",
          },
        }}
      >
        +{optimizedUsers.length - max}
      </Box>
    )
  ) : null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: -1 }}>
      {visibleUsers.map((user, index) => {
        return showTooltip ? (
          <Tooltip
            key={index}
            title={user.displayName || user.email}
            arrow
            placement="top"
          >
            <Avatar
              key={index}
              email={user.email}
              auth0Picture={user.auth0Picture}
              alt={user.displayName || user.email}
              sx={{
                ...avatarSx,
                border: `2px solid white`,
                marginLeft: index > 0 ? -1 : 0,
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  zIndex: 10,
                  transform: "scale(1.1)",
                },
              }}
              showSkeleton={false}
              variant={variant}
            />
          </Tooltip>
        ) : (
          <Avatar
            key={index}
            email={user.email}
            auth0Picture={user.auth0Picture}
            alt={user.displayName || user.email}
            sx={{
              ...avatarSx,
              border: `2px solid white`,
              marginLeft: index > 0 ? -1 : 0,
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                zIndex: 10,
                transform: "scale(1.1)",
              },
            }}
            showSkeleton={false}
            variant={variant}
          />
        );
      })}
      {overflowAvatar}
    </Box>
  );
};

export default AvatarStack;
